import {
  Environment
} from '@vodacom/web-env';

import {
  AppState,
  mapCities,
  mapConfig,
  mapFaqContent,
  mapMenu,
  mapPage,
  mapPillarContent,
  mapPillarMeta,
  mapPromotionBanner,
  mapPromotions,
  mapRssContent,
  mapTemplates,
  mapWhatsNewContent
} from '../helpers';

import {
  CacheKeys,
  JavascriptTypes
} from '../constants';

export class DrupalProvider {

  static BASE_URL = Environment.current.drupalBaseUrl;
  static DEFAULT_HEADERS = {
    'Accept': 'application/vnd.api+json',
    'Content-Type': 'application/vnd.api+json'
  };
  static STATE = AppState.with('DrupalProvider');

  static async getPageTemplates() {
    return DrupalProvider.STATE.makeServiceCall(
      `${DrupalProvider.BASE_URL}/jsonapi/node/page_template`,
      {
        method: 'GET',
        headers: {
          ...DrupalProvider.DEFAULT_HEADERS
        },
        queryParams: {
          'fields[node--page_template]': 'title,field_section',
          'fields[paragraph--component_section]': 'field_component,field_source,field_location'
        },
        cacheKey: CacheKeys.PageTemplates,
        mapper: mapTemplates
      }
    )
      .then((res) => {
        if (!res.result || !Array.isArray(res.result)) {
          return {
            ok: false,
            error: new Error('No templates found'),
            trace: null,
            date: null,
            result: null,
            code: null,
            stamp: new Date()
          };
        }

        return res;
      })
      .catch((error) => {
        return {
          ok: false,
          error: error,
          trace: null,
          date: null,
          result: null,
          code: null,
          stamp: new Date()
        };
      });
  }

  static getPillarTiles(pillar) {
    return DrupalProvider.STATE.makeServiceCall(
      `${DrupalProvider.BASE_URL}/jsonapi/views/pillar_content_list/default`,
      {
        method: 'GET',
        headers: {
          ...DrupalProvider.DEFAULT_HEADERS
        },
        queryParams: {
          'views-filter[name]': pillar.toLowerCase(),
          'include':
            'field_pillar_category,field_service_picture,field_template,field_additional_buttons',
          'fields[node--pillar_content]':
            'title,field_short_description,field_click_out_url,field_pillar_content_button_text,field_has_connectu_page,' +
            'field_is_zero_rated,field_full_content,field_template,' +
            'field_pillar_category,field_service_picture,field_optional_link_android,field_optional_link_ios,field_additional_buttons,' +
            'field_is_subscription,field_vodapay_app_id,field_hide_on_vodapay',
          'fields[taxonomy_term--field_pillar_category]': 'name',
          'fields[file--file]': 'uri',
          'fields[node--field_template]': 'title',
          'fields[paragraph--button]': 'field_button_text,field_click_out_url,field_is_url_zero_rated,field_vodapay_app_id'
        },
        cacheKey: `${CacheKeys.Pillars}${pillar.toLowerCase()}`,
        mapper: mapPillarContent
      }
    )
      .then((res) => {
        if (!res.result || !Array.isArray(res.result)) {
          return {
            ok: false,
            error: new Error('No tiles found'),
            trace: null,
            date: null,
            result: null,
            code: null,
            stamp: new Date()
          };
        }

        return res;
      })
      .catch((error) => {
        return {
          ok: false,
          error: error,
          trace: null,
          date: null,
          result: null,
          code: null,
          stamp: new Date()
        };
      });
  }

  static getWhatsNew() {

    return DrupalProvider.STATE.makeServiceCall(
      `${DrupalProvider.BASE_URL}/jsonapi/node/whats_new`,
      {
        method: 'GET',
        headers: {
          ...DrupalProvider.DEFAULT_HEADERS
        },
        queryParams: {
          'fields[node--whats_new]': 'title,field_topic',
          'fields[paragraph--topic]': 'created,field_title,field_main_content,field_url,field_summary,field_image,'+
          'field_hasconnectu,field_is_zero_rated,field_mini_app_appid,field_hide_on_vodapay',
          'fields[file--file]': 'uri'
        },
        cacheKey: CacheKeys.WhatsNew,
        mapper: mapWhatsNewContent
      }
    )
      .then((res) => {

        if (!res.result || !Array.isArray(res.result)) {
          return {
            ok: false,
            error: new Error('No topics found'),
            trace: null,
            date: null,
            result: null,
            code: null,
            stamp: new Date()
          };
        }

        return res;
      })
      .catch((error) => {
        return {
          ok: false,
          error: error,
          trace: null,
          date: null,
          result: null,
          code: null,
          stamp: new Date()
        };
      });
  }

  static getConfig(context) {

    return DrupalProvider.STATE.makeServiceCall(
      `${DrupalProvider.BASE_URL}/jsonapi/node/config`,
      {
        method: 'GET',
        headers: {
          ...DrupalProvider.DEFAULT_HEADERS
        },
        queryParams: {
          'include': 'field_voucher_advance_config,field_advance_prompt_config,field_mini_apps',
          'fields[node--config]': 'field_enable_top_banner,field_top_banner_content,' +
            'field_enable_whats_new_banner,field_whats_new_banner_content,field_whats_new_banner_title,' +
            'field_j4u_error_message,field_feature_toggles,field_enable_home_notification,field_home_notification,' +
            'field_fair_usage_policy,field_voucher_advance_config,field_advance_prompt_config,field_mini_apps',
          'fields[paragraph--voucher_config]': 'field_eligible_hero_description_,field_ineligible_hero_descriptio,' +
            'field_payment_success_message,field_voucher_share_message,field_confirm_order_title,field_confirm_order_description,' +
            'field_advance_success_message,field_buy_now_banner,field_voucher_redeem_text,field_outlets_unavailable_error,' +
            'field_vouchers_unavailable_error,field_purchase_history_error,field_no_past_purchases',
          'fields[paragraph--advance_prompt_config]': 'field_minimum_airtime_balance,field_minimum_data_balance,' +
            'field_prompt_header_text,field_prompt_body_text',
          'fields[paragraph--mini_app]': 'field_mini_app_name,field_mini_app_appid',
          'filter[title][value]': context
        },
        cacheKey: CacheKeys.Config,
        mapper: mapConfig
      }
    )
      .then((res) => {

        if (!res?.result || !Array.isArray(res?.result)) {
          return {
            ok: false,
            error: new Error('No config found'),
            trace: null,
            date: null,
            result: null,
            code: null,
            stamp: new Date()
          };
        }

        return res;
      })
      .catch((error) => {

        return {
          ok: false,
          error: error,
          trace: null,
          date: null,
          result: null,
          code: null,
          stamp: new Date()
        };
      });
  }

  static getCities() {

    return DrupalProvider.STATE.makeServiceCall(
      `${DrupalProvider.BASE_URL}/jsonapi/node/cities`,
      {
        method: 'GET',
        headers: {
          ...DrupalProvider.DEFAULT_HEADERS
        },
        queryParams: {
          'fields[node--cities]': 'field_cities',
          'include': 'field_cities',
          'fields[paragraph--city]': 'field_name,field_latitude,field_longitude,field_code'
        },
        cacheKey: CacheKeys.Cities,
        mapper: mapCities
      }
    )
      .then((res) => {
        if (!res?.result || !Array.isArray(res?.result)) {
          return {
            ok: false,
            error: new Error('No cities found'),
            trace: null,
            date: null,
            result: null,
            code: null,
            stamp: new Date()
          };
        }

        return res;
      })
      .catch((error) => {
        return {
          ok: false,
          error: error,
          trace: null,
          date: null,
          result: null,
          code: null,
          stamp: new Date()
        };
      });
  }

  static getRssFeeds() {

    return DrupalProvider.STATE.makeServiceCall(
      `${DrupalProvider.BASE_URL}/jsonapi/views/feed_tabs/default`,
      {
        method: 'GET',
        headers: {
          ...DrupalProvider.DEFAULT_HEADERS
        },
        queryParams: {
          'fields[node--rss_content]': 'title,field_feeds',
          'fields[paragraph--rss_feed]': 'field_company,field_feed_url,field_default_image',
          'include': 'field_feeds,field_feeds.field_default_image',
          'fields[file--file]': 'uri'
        },
        cacheKey: CacheKeys.RssFeeds,
        mapper: mapRssContent
      }
    )
      .then((res) => {
        if (!res.result || !Array.isArray(res.result)) {
          return {
            ok: false,
            error: new Error('No tiles found'),
            trace: null,
            date: null,
            result: null,
            code: null,
            stamp: new Date()
          };
        }

        return res;
      })
      .catch((error) => {
        return {
          ok: false,
          error: error,
          trace: null,
          date: null,
          result: null,
          code: null,
          stamp: new Date()
        };
      });
  }

  static getPillarMeta() {

    return DrupalProvider.STATE.makeServiceCall(
      `${DrupalProvider.BASE_URL}/jsonapi/views/pillar_list/default`,
      {
        method: 'GET',
        headers: {
          ...DrupalProvider.DEFAULT_HEADERS
        },
        queryParams: {
          'fields[node--pillar]': 'title,field_pillar_description,' +
          'field_pillar_hero,field_pillar_term,field_pillar_card',
          'include': 'field_pillar_hero,field_pillar_term,field_pillar_card',
          'fields[file--file]': 'uri',
          'fields[taxonomy_term--pillar_type]': 'name'
        },
        cacheKey: CacheKeys.PillarMeta,
        mapper: mapPillarMeta
      }
    )
      .then((res) => {

        if (!res.result || !Array.isArray(res.result)) {
          return {
            ok: false,
            error: new Error('No pillar meta found'),
            trace: null,
            date: null,
            result: null,
            code: null,
            stamp: new Date()
          };
        }

        return res;
      })
      .catch((error) => {
        return {
          ok: false,
          error: error,
          trace: null,
          date: null,
          result: null,
          code: null,
          stamp: new Date()
        };
      });
  }

  static getPages() {

    return DrupalProvider.STATE.makeServiceCall(
      `${DrupalProvider.BASE_URL}/jsonapi/node/page`,
      {
        method: 'GET',
        headers: { ...DrupalProvider.DEFAULT_HEADERS },
        queryParams: {
          'fields[node--page]': 'title,body,path,field_sub_pages',
          'fields[paragraph--sub_pages]': 'field_page_content'
        },
        cacheKey: CacheKeys.Pages,
        mapper: mapPage
      }
    )
      .then((res) => {

        if (!res.result || !Array.isArray(res.result)) {
          return {
            ok: false,
            error: new Error('Page data not found'),
            trace: null,
            date: null,
            result: null,
            code: null,
            stamp: new Date()
          };
        }

        return res;
      })
      .catch((error) => {
        return {
          ok: false,
          error: error,
          trace: null,
          date: null,
          result: null,
          code: null,
          stamp: new Date()
        };
      });
  }

  static getMenu() {

    return DrupalProvider.STATE.makeServiceCall(
      `${DrupalProvider.BASE_URL}/jsonapi/menu_items/connectu`,
      {
        method: 'GET',
        headers: { ...DrupalProvider.DEFAULT_HEADERS },
        cacheKey: CacheKeys.Menu,
        mapper: mapMenu
      }
    )
      .then((res) => {

        if (!res.result || !Array.isArray(res.result)) {
          return {
            ok: false,
            error: new Error('Menu data not found'),
            trace: null,
            date: null,
            result: null,
            code: null,
            stamp: new Date()
          };
        }

        return res;
      })
      .catch((error) => {

        return {
          ok: false,
          error: error,
          trace: null,
          date: null,
          result: null,
          code: null,
          stamp: new Date()
        };
      });
  }

  static getFaqPages() {

    return DrupalProvider.STATE.makeServiceCall(
      `${DrupalProvider.BASE_URL}/jsonapi/node/faqs`,
      {
        method: 'GET',
        headers: {
          ...DrupalProvider.DEFAULT_HEADERS
        },
        queryParams: {
          'fields[node--faqs]': 'title,field_faq_item,field_header,field_header_image',
          'fields[paragraph--faq_item]': 'field_header,field_content'
        },
        cacheKey: CacheKeys.FaqPages,
        mapper: mapFaqContent
      }
    )
      .then((res) => {
        if (!res.result || !Array.isArray(res.result)) {
          return {
            ok: false,
            error: new Error('No tiles found'),
            trace: null,
            date: null,
            result: null,
            code: null,
            stamp: new Date()
          };
        }

        return res;
      })
      .catch((error) => {
        return {
          ok: false,
          error: error,
          trace: null,
          date: null,
          result: null,
          code: null,
          stamp: new Date()
        };
      });
  }

  static getInstitutions() {

    return {
      ok: true,
      result: [
        { value: 'University of Pretoria', label: 'University of Pretoria' },
        { value: 'University of Flamingo', label: 'University of Flamingo' },
        { value: 'University of Easter', label: 'University of Easter' },
        { value: 'University of Cape Town', label: 'University of Cape Town' }
      ]
    };
  }

  static getPromotionBanner() {

    return DrupalProvider.STATE.makeServiceCall(
      `${DrupalProvider.BASE_URL}/jsonapi/node/promotion_banner`,
      {
        method: 'GET',
        headers: {
          ...DrupalProvider.DEFAULT_HEADERS
        },
        queryParams: {
          'fields[node--promotion_banner]': 'title,field_promotion_banner',
          'include': 'field_promotion_banner,field_promotion_banner.field_image'
        },
        cacheKey: CacheKeys.PromotionBanner,
        mapper: mapPromotionBanner
      }
    )
      .then((res) => {

        if (!res.result || !Array.isArray(res.result)) {
          return {
            ok: false,
            error: new Error('No promotion found'),
            trace: null,
            date: null,
            result: null,
            code: null,
            stamp: new Date()
          };
        }

        return res;
      })
      .catch((error) => {
        return {
          ok: false,
          error: error,
          trace: null,
          date: null,
          result: null,
          code: null,
          stamp: new Date()
        };
      });
  }

  static getPromotions() {

    return DrupalProvider.STATE.makeServiceCall(
      `${DrupalProvider.BASE_URL}/jsonapi/node/promotions`,
      {
        method: 'GET',
        headers: {
          ...DrupalProvider.DEFAULT_HEADERS
        },
        queryParams: {
          'fields[node--promotions]': 'title,field_promotions',
          'include': 'field_promotions,field_promotions.field_image',
          'fields[paragraph--promotion]': 'field_image,field_url,field_title',
          'fields[file--file]': 'uri'
        },
        cacheKey: CacheKeys.Promotions,
        mapper: mapPromotions
      }
    )
      .then((res) => {

        if (!res.result || !Array.isArray(res.result)) {
          return {
            ok: false,
            error: new Error('No promotions found'),
            trace: null,
            date: null,
            result: null,
            code: null,
            stamp: new Date()
          };
        }

        return res;
      })
      .catch((error) => {
        return {
          ok: false,
          error: error,
          trace: null,
          date: null,
          result: null,
          code: null,
          stamp: new Date()
        };
      });
  }

  static async getCachedItem(key, fallback, serviceFallback) {

    const cachedItem = DrupalProvider.STATE.getItem(key, fallback);

    if (!cachedItem && serviceFallback && typeof serviceFallback === JavascriptTypes.Function) {
      const response = await serviceFallback();

      if (response.ok && response.result) {
        return response;
      }
    }

    return cachedItem;
  }
}
